import { errorMessage } from "../utils/errorMessage";
import { Form } from "../../../../../../shared/frontend/ui";
import React from "react";
import { ThemedModal } from "../../components/ThemedModal";
import { useAuth } from "../auth/useAuth";
import { spacing } from "../design-system";
import { useI18n } from "../i18n/useI18n";
import { SecretInput } from "../../components/SecretInput";

const PASSWORD_MIN_LENGTH = 8;

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ChangePasswordModal({ open, onClose }: Props) {
  const auth = useAuth();
  const { text } = useI18n();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const rulesDeEn = [
    { required: true, message: text("Please enter password") },
    { min: PASSWORD_MIN_LENGTH, message: text("At least 8 characters") },
    {
      pattern: /[A-Z]/,
      message: text("At least one uppercase letter")
    },
    {
      pattern: /[a-z]/,
      message: text("At least one lowercase letter")
    },
    {
      pattern: /\d/,
      message: text("At least one digit")
    },
    {
      pattern: /[!@#$%^&*()_+\-=[\]{}|;:',.<>?/~`"\\]/,
      message: text("At least one special character (!@#$%^&*...)")
    }
  ];

  const handleSubmit = async () => {
    setError(null);
    const values = await form.validateFields();
    if (values.newPassword !== values.newPasswordConfirm) {
      setError(text("Passwords do not match"));
      return;
    }
    setLoading(true);
    try {
      await auth.api.changePassword(values.currentPassword, values.newPassword);
      form.resetFields();
      onClose();
    } catch (e: unknown) {
      setError(errorMessage(e, text("Error changing password")));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setError(null);
    onClose();
  };

  return (
    <ThemedModal
      title={text("Change password")}
      open={open}
      onCancel={handleClose}
      formId="m1member-change-password-form"
      submitting={loading}
      destroyOnClose
      okText={text("Change")}
      cancelText={text("Cancel")}
    >
      <Form
        id="m1member-change-password-form"
        name="m1member-change-password-form"
        form={form}
        layout="vertical"
        style={{ marginTop: spacing.lg }}
        onFinish={handleSubmit}
      >
        {error ? (
          <div style={{ color: "var(--ant-color-error)", marginBottom: spacing.md }}>{error}</div>
        ) : null}
        <Form.Item
          name="currentPassword"
          label={text("Current password")}
          rules={[{ required: true, message: text("Please enter current password") }]}
        >
          <SecretInput
            autoComplete="section-m1member current-password"
            id="m1member-current-password"
            name="m1member-current-password"
          />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label={text("New password")}
          rules={rulesDeEn}
          help={text("Min. 8 chars, upper/lowercase, digit, special char")}
        >
          <SecretInput
            autoComplete="section-m1member new-password"
            id="m1member-new-password"
            name="m1member-new-password"
          />
        </Form.Item>
        <Form.Item
          name="newPasswordConfirm"
          label={text("Confirm new password")}
          rules={[{ required: true, message: text("Please confirm password") }]}
        >
          <SecretInput
            autoComplete="section-m1member new-password"
            id="m1member-new-password-confirm"
            name="m1member-new-password-confirm"
          />
        </Form.Item>
      </Form>
    </ThemedModal>
  );
}
